import { useEffect, useRef, useState } from "react";
import { Lock, Music2, GraduationCap, Loader2 } from "lucide-react";
import { API } from "@/assets";
import { useSiteAuth } from "@/components/GoogleAuth";
import { MedusaEngine } from "@/components/medusa/engine";
import { SectionShell } from "@/components/medusa/SectionShell";
import { LessonRunner } from "@/components/medusa/LessonRunner";
import { SongPerformance } from "@/components/medusa/SongPerformance";

export default function PianoMasteryPage() {
  const { profile } = useSiteAuth();
  const engine = useRef(null);
  if (!engine.current && typeof window !== "undefined") engine.current = new MedusaEngine();

  const [content, setContent] = useState(null);
  const [mode, setMode] = useState("lessons"); // lessons | songs
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeSong, setActiveSong] = useState(null);
  const [progress, setProgress] = useState({});

  const playerLevel = profile?.level || 1;

  useEffect(() => {
    fetch(`${API}/academy/content`).then((r) => r.json()).then(setContent).catch(() => setContent({ lessons: [], songs: [] }));
  }, []);
  useEffect(() => {
    if (!profile) { setProgress({}); return; }
    fetch(`${API}/players/progress`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { progress: [] }))
      .then((d) => {
        const map = {};
        (d.progress || []).forEach((p) => { map[`${p.ref_type}:${p.ref_id}`] = p; });
        setProgress(map);
      })
      .catch(() => {});
  }, [profile]);

  return (
    <SectionShell
      kicker="Section I · Piano Mastery"
      title="Piano Mastery"
      blurb="Real timing, accuracy, and reading — built lesson by lesson, then proven on stage in Song Performance Mode. Touch the keys, click them, or play A W S E D F T G Y H U J K O L."
      color="#FF3399"
      testId="piano-mastery"
    >
      <div className="grid grid-cols-2 gap-2 mb-6 max-w-sm" data-testid="piano-mode-tabs">
        {[["lessons", "Curriculum", GraduationCap], ["songs", "Song Performance", Music2]].map(([m, label, Icon]) => (
          <button key={m} onClick={() => setMode(m)}
            className={`inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-4 py-2.5 border transition-all ${mode === m ? "bg-pink-500 text-white border-pink-500" : "border-white/15 text-ice/55 hover:border-pink-400/60"}`}
            data-testid={`piano-mode-${m}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {content === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-pink-400" size={26} /></div>
      ) : mode === "lessons" ? (
        activeLesson ? (
          <LessonRunner lesson={activeLesson} engine={engine} onClose={() => setActiveLesson(null)}
            onComplete={() => { /* profile refresh happens via PlayerCard context on next XP grant */ }} />
        ) : (
          <div className="space-y-3" data-testid="lesson-list">
            {content.lessons.map((l) => {
              const locked = playerLevel < l.unlock_level;
              const cleared = progress[`lesson:${l.lesson_id}`];
              return (
                <button key={l.lesson_id} disabled={locked}
                  onClick={() => setActiveLesson(l)}
                  className={`w-full text-left rounded-2xl border p-5 transition-all active:scale-[0.99] ${locked ? "border-[#26262c] bg-[#0c0c10] opacity-60 cursor-not-allowed" : "border-pink-500/25 bg-[#0B0B10]/80 hover:border-pink-400/60 hover:shadow-[0_0_24px_rgba(255,51,153,0.15)]"}`}
                  data-testid={`lesson-card-${l.lesson_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold shrink-0 ${locked ? "bg-white/5 text-ice/30" : "bg-pink-500/15 text-pink-300"}`}>
                      {locked ? <Lock size={15} /> : l.order}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-white text-sm font-bold">{l.title}</p>
                      <p className="text-xs text-ice/50 mt-0.5 line-clamp-2">{l.blurb}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-yellow-300/80">+{l.xp} XP</p>
                      {cleared && <p className="font-mono text-[9px] text-emerald-300 mt-1" data-testid={`lesson-best-${l.lesson_id}`}>best {cleared.best_score}%</p>}
                      {locked && <p className="font-mono text-[9px] text-ice/35 mt-1">unlocks Lv {l.unlock_level}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        activeSong ? (
          <SongPerformance song={activeSong} engine={engine} onClose={() => setActiveSong(null)} />
        ) : (
          <div className="space-y-3" data-testid="song-list">
            {content.songs.map((s) => {
              const locked = playerLevel < s.unlock_level;
              const best = progress[`song:${s.song_id}`];
              return (
                <button key={s.song_id} disabled={locked}
                  onClick={() => setActiveSong(s)}
                  className={`w-full text-left rounded-2xl border p-5 transition-all active:scale-[0.99] ${locked ? "border-[#26262c] bg-[#0c0c10] opacity-60 cursor-not-allowed" : "border-fuchsia-500/25 bg-[#0B0B10]/80 hover:border-fuchsia-400/60 hover:shadow-[0_0_24px_rgba(217,70,239,0.15)]"}`}
                  data-testid={`song-card-${s.song_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${locked ? "bg-white/5 text-ice/30" : "bg-fuchsia-500/15 text-fuchsia-300"}`}>
                      {locked ? <Lock size={15} /> : <Music2 size={16} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-white text-sm font-bold">{s.title}</p>
                      <p className="text-xs text-ice/50 mt-0.5">{s.artist} · {s.difficulty} · {s.bpm} BPM · {s.chart.length} notes</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-yellow-300/80">+{s.xp} XP</p>
                      {best && <p className="font-mono text-[9px] text-emerald-300 mt-1" data-testid={`song-best-${s.song_id}`}>best {best.best_score.toLocaleString()}</p>}
                      {locked && <p className="font-mono text-[9px] text-ice/35 mt-1">unlocks Lv {s.unlock_level}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </SectionShell>
  );
}
